import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiGrid,
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiInfo,
  FiRefreshCw,
  FiUploadCloud,
  FiX,
  FiExternalLink,
  FiLayers,
} from "react-icons/fi";
import {
  getMySpaceInteriorCatalogs,
  getInteriorCatalogTypes,
  addInteriorCatalog,
  editInteriorCatalog,
  deleteInteriorCatalog,
  getInteriorCatalogDetails,
  uploadMediaFiles,
} from "../../../api/partner/partnerFeatureApis";

export default function PartnerInteriorDesignHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;

  const [catalogs, setCatalogs] = useState([]);
  const [catalogTypes, setCatalogTypes] = useState([]);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Add / Edit Modal state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCatalogId, setEditingCatalogId] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Details Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Fetch Catalogs
  const fetchCatalogs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAlertMsg(null);
    try {
      const data = await getMySpaceInteriorCatalogs(token, { page_number: 1, page_size: 50 });
      const items = data?.catalogs || data?.interiorCatalogs || [];
      setCatalogs(items);
    } catch (err) {
      console.error("Failed to load interior catalogs:", err);
      setAlertMsg({ type: "danger", text: "Failed to load interior catalogs. Please refresh." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch Catalog Types
  const fetchCatalogTypes = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getInteriorCatalogTypes(token);
      setCatalogTypes(data?.catalogTypes || []);
    } catch (err) {
      console.error("Failed to load catalog types:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchCatalogs();
    fetchCatalogTypes();
  }, [fetchCatalogs, fetchCatalogTypes]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setEditingCatalogId(null);
    setSelectedTypeId(catalogTypes[0]?.id ? String(catalogTypes[0].id) : "");
    setPrice("");
    setDescription("");
    setWebsiteUrl("");
    setSelectedFiles([]);
    setExistingImages([]);
    setFormError("");
    setShowAddEditModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (catalog) => {
    setIsEditMode(true);
    setEditingCatalogId(catalog.id);
    setSelectedTypeId(catalog.type?.id ? String(catalog.type.id) : "");
    setPrice(catalog.price ? catalog.price.replace(/[^\d]/g, "") : "");
    setDescription(catalog.description || "");
    setWebsiteUrl(catalog.url || "");
    setSelectedFiles([]);
    setExistingImages(catalog.catalog_images || []);
    setFormError("");
    setShowAddEditModal(true);
  };

  // Delete Catalog
  const handleDeleteCatalog = async (catalogId) => {
    if (!window.confirm("Are you sure you want to permanently delete this design catalog?")) {
      return;
    }
    setActionLoadingId(catalogId);
    try {
      const res = await deleteInteriorCatalog(token, catalogId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Design catalog deleted successfully." });
        setCatalogs((prev) => prev.filter((c) => c.id !== catalogId));
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to delete catalog." });
      }
    } catch (err) {
      setAlertMsg({ type: "danger", text: err?.response?.data?.text || "Failed to delete catalog." });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Open Details Modal
  const handleOpenDetails = async (catalogId) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const data = await getInteriorCatalogDetails(token, catalogId);
      setSelectedCatalog(data?.catalog || data?.interiorCatalog || null);
    } catch (err) {
      console.error("Failed to load catalog details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!selectedTypeId || !price || !description) {
      setFormError("Please fill in catalog type, price, and description.");
      return;
    }

    if (!isEditMode && selectedFiles.length === 0) {
      setFormError("Please select at least one image or video for the catalog.");
      return;
    }

    setSubmitting(true);
    try {
      let uploadedImages = [];
      if (selectedFiles.length > 0) {
        const uploadRes = await uploadMediaFiles(token, selectedFiles);
        const propImages = uploadRes?.propertyImages || [];
        uploadedImages = propImages.map((img) => ({
          imagePath: img.imagePath,
          image_url: img.image_url,
          home: false,
          active: true,
          deleted: false,
        }));
      }

      const allImages = isEditMode
        ? [...existingImages, ...uploadedImages]
        : uploadedImages;

      const numericPrice = price.replace(/[^\d]/g, "");
      const formattedPrice = `${Number(numericPrice).toLocaleString("en-IN")} INR`;

      const payload = {
        price: formattedPrice,
        description,
        url: websiteUrl || "",
        type: { id: Number(selectedTypeId) },
        catalog_images: allImages,
        catalog_videos: [],
      };

      if (isEditMode) {
        payload.id = editingCatalogId;
        const res = await editInteriorCatalog(token, payload);
        if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
          setAlertMsg({ type: "success", text: "Catalog updated successfully!" });
          setShowAddEditModal(false);
          fetchCatalogs();
        } else {
          setFormError(res?.text || "Failed to update catalog.");
        }
      } else {
        const res = await addInteriorCatalog(token, payload);
        if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
          setAlertMsg({ type: "success", text: "New interior catalog published successfully!" });
          setShowAddEditModal(false);
          fetchCatalogs();
        } else {
          setFormError(res?.text || "Failed to publish catalog.");
        }
      }
    } catch (err) {
      setFormError(err?.response?.data?.text || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered Catalogs
  const filteredCatalogs = catalogs.filter((item) => {
    if (selectedTypeFilter && String(item.type?.id) !== String(selectedTypeFilter)) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const desc = (item.description || "").toLowerCase();
    const typeName = (item.type?.name || "").toLowerCase();
    return desc.includes(q) || typeName.includes(q);
  });

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiLayers className="text-primary" /> Interior Design Portfolio & Catalogs
          </h3>
          <p className="text-muted mb-0 small">
            Showcase your interior architecture designs, 3D renders, decor projects, and pricing to verified property owners.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
            onClick={fetchCatalogs}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2 btn-sm px-3 shadow-sm"
            onClick={handleOpenAdd}
          >
            <FiPlus /> Add Design Catalog
          </button>
        </div>
      </div>

      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-4">
              <select
                className="form-select form-select-sm"
                value={selectedTypeFilter}
                onChange={(e) => setSelectedTypeFilter(e.target.value)}
              >
                <option value="">All Design Categories</option>
                {catalogTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-8">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search designs by description or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Catalogs Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading catalogs...</span>
          </div>
          <p className="text-muted mt-2 small">Loading design portfolio...</p>
        </div>
      ) : filteredCatalogs.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiLayers size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Design Catalogs Found</h5>
            <p className="text-muted small mb-3">
              {searchQuery
                ? `No catalogs matching "${searchQuery}".`
                : "You haven't added any interior design projects yet. Click 'Add Design Catalog' to start showcasing your work."}
            </p>
            <button className="btn btn-primary btn-sm" onClick={handleOpenAdd}>
              Add Your First Catalog
            </button>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {filteredCatalogs.map((catalog) => {
            const images = catalog.catalog_images || [];
            const thumbnail = images[0]?.image_url
              ? (images[0].image_url.startsWith("http")
                  ? images[0].image_url
                  : `https://images.allhomedesire.com${images[0].image_url}`)
              : null;
            const isBusy = actionLoadingId === catalog.id;

            return (
              <div key={catalog.id} className="col-12 col-sm-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden d-flex flex-column justify-content-between">
                  <div>
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt="Catalog Design"
                        className="w-100 object-fit-cover"
                        style={{ height: "180px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="w-100 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center text-muted"
                        style={{ height: "180px" }}
                      >
                        <FiLayers size={40} />
                      </div>
                    )}

                    <div className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary">
                          {catalog.type?.name || "Interior Design"}
                        </span>
                        <h5 className="fw-bold text-dark mb-0 fs-6">
                          {catalog.price || "Price on Request"}
                        </h5>
                      </div>

                      <p
                        className="text-secondary small mb-3 line-clamp-3"
                        style={{ minHeight: "54px" }}
                      >
                        {catalog.description || "Custom interior design and furnishing solution."}
                      </p>

                      {catalog.url && (
                        <a
                          href={catalog.url.startsWith("http") ? catalog.url : `https://${catalog.url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="small text-primary d-inline-flex align-items-center gap-1 text-decoration-none"
                        >
                          <FiExternalLink size={12} /> Project Portfolio Link
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="p-3 pt-0 border-top mt-2">
                    <div className="d-flex gap-2 pt-2">
                      <button
                        className="btn btn-outline-secondary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                        onClick={() => handleOpenDetails(catalog.id)}
                      >
                        <FiInfo size={13} /> View
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                        onClick={() => handleOpenEdit(catalog)}
                      >
                        <FiEdit size={13} /> Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm px-3 d-flex align-items-center justify-content-center gap-1"
                        onClick={() => handleDeleteCatalog(catalog.id)}
                        disabled={isBusy}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          ADD / EDIT CATALOG MODAL
          ========================================================================= */}
      {showAddEditModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <form onSubmit={handleSubmit}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <FiLayers className="text-primary" />
                    {isEditMode ? "Edit Interior Design Catalog" : "Add New Interior Design Catalog"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddEditModal(false)}
                    disabled={submitting}
                  />
                </div>

                <div className="modal-body p-4">
                  {formError && (
                    <div className="alert alert-danger py-2 small mb-3">{formError}</div>
                  )}

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Catalog Category / Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedTypeId}
                        onChange={(e) => setSelectedTypeId(e.target.value)}
                        required
                      >
                        <option value="">Select Category</option>
                        {catalogTypes.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Estimated Price (INR) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 250000"
                        value={price}
                        onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Project Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Detail the materials, design theme (e.g. Minimalist, Contemporary), scope of work..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Project Website or 3D Walkthrough URL
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://..."
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Upload Photos / Media {!isEditMode && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept="image/*,video/*"
                      onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                    />
                    <div className="form-text small">
                      Select high resolution renderings, site photos, or walkthrough videos.
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowAddEditModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={submitting}
                  >
                    {submitting ? "Uploading & Saving..." : isEditMode ? "Save Changes" : "Publish Catalog"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          CATALOG DETAILS MODAL
          ========================================================================= */}
      {showDetailsModal && selectedCatalog && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  {selectedCatalog.type?.name || "Design Catalog Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                {detailsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <p className="text-muted small mt-2">Loading details...</p>
                  </div>
                ) : (
                  <div>
                    {selectedCatalog.catalog_images?.length > 0 && (
                      <div className="d-flex gap-2 overflow-auto pb-2 mb-3">
                        {selectedCatalog.catalog_images.map((img, i) => {
                          const src = img.image_url?.startsWith("http")
                            ? img.image_url
                            : `https://images.allhomedesire.com${img.image_url}`;
                          return (
                            <img
                              key={i}
                              src={src}
                              alt="Design"
                              className="rounded-3 object-fit-cover shadow-sm"
                              style={{ width: "220px", height: "150px" }}
                            />
                          );
                        })}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="badge bg-primary bg-opacity-10 text-primary fs-6">
                        {selectedCatalog.type?.name}
                      </span>
                      <h4 className="fw-bold text-dark mb-0">{selectedCatalog.price}</h4>
                    </div>

                    <h6 className="fw-bold text-dark mb-1">Description</h6>
                    <p className="text-secondary small mb-3" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedCatalog.description}
                    </p>

                    {selectedCatalog.url && (
                      <div className="mb-3">
                        <a
                          href={selectedCatalog.url.startsWith("http") ? selectedCatalog.url : `https://${selectedCatalog.url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                        >
                          <FiExternalLink size={14} /> Visit Project Portfolio
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
